import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Lightbulb, CaretDown, CaretUp, Sparkle, CurrencyJpy, EyeSlash, BookOpen, Files, ArrowSquareOut,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { devApi } from './devApi';
import { KnowledgeDraftDialog } from './KnowledgeDraftDialog';

const yen = (v) => `¥${Math.round(v || 0).toLocaleString('ja-JP')}`;

const SEV = {
  high: { label: '重要', cls: 'border-rose-300 bg-rose-50 text-rose-900' },
  medium: { label: '注意', cls: 'border-amber-300 bg-amber-50 text-amber-900' },
  low: { label: '参考', cls: 'border-zinc-300 bg-white text-zinc-700' },
};

/** 過去の学び — 事実はデータから、コメントだけAI。AIが無くても事実は必ず出る。 */
export const LearningCard = ({ devId, developmentType, partNames = [], context = 'detail',
  compact = false, canEdit = false, testid = 'dev-learning-card' }) => {
  const [d, setD] = useState(null);
  const [k, setK] = useState(null);
  const [open, setOpen] = useState(context !== 'detail');
  const [showAll, setShowAll] = useState(false);
  const [draft, setDraft] = useState(null);

  const names = partNames.filter(Boolean).join(',');
  const load = useCallback(() => {
    devApi.learningRecommendations({
      development_id: devId, part_names: names, development_type: developmentType, context,
    }).then(setD).catch(() => {});
  }, [devId, names, developmentType, context]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!devId || compact) return;
    devApi.knowledgeSuggestions(devId).then(setK).catch(() => {});
  }, [devId, compact]);

  const dismiss = async (key) => {
    try {
      await devApi.dismissWarning({ development_id: devId, key });
      toast.success('この案件では表示しません');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || '操作できませんでした'); }
  };
  const restore = async () => {
    try {
      const all = await devApi.learningRecommendations({
        development_id: devId, context, include_dismissed: true,
      });
      await Promise.all((all.dismissed_keys || []).map((key) =>
        devApi.restoreWarning({ development_id: devId, key })));
      toast.success('非表示を解除しました');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || '操作できませんでした'); }
  };

  if (!d || !d.has_learning) return null;
  const warnings = showAll ? d.warnings : d.top_warnings;
  const summary = [
    d.warnings.length ? `注意点 ${d.warnings.length}件` : null,
    d.similar_projects.length ? `近い過去案件 ${d.similar_projects.length}件` : null,
    !compact && k?.hits?.length ? `関連資料 ${k.hits.length}件` : null,
  ].filter(Boolean).join(' · ');

  return (
    <section className="rounded-lg border border-amber-300 bg-amber-50/60 shadow-sm" data-testid={testid}>
      <button type="button" onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-4 py-2 text-left"
        data-testid={`${testid}-toggle`}>
        <Lightbulb size={16} className="text-amber-700" />
        <span className="text-sm font-bold text-zinc-900">過去の学び</span>
        <span className="text-xs text-zinc-600">{summary}</span>
        <span className="ml-auto text-zinc-600">
          {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-amber-200 px-4 py-3" data-testid={`${testid}-body`}>
          {warnings.length > 0 && (
            <ul className="space-y-1.5" data-testid={`${testid}-warnings`}>
              {warnings.map((w) => (
                <li key={w.key}
                  className={`rounded-md border px-3 py-2 ${SEV[w.severity].cls}`}
                  data-testid={`${testid}-warning-${w.key}`}>
                  <div className="flex items-start gap-2">
                    <span className="shrink-0 rounded-full border border-current px-1.5 text-[10px] font-bold">
                      {SEV[w.severity].label}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold">{w.title}</p>
                      {w.detail && <p className="text-xs opacity-80">{w.detail}</p>}
                      <p className="text-xs font-medium text-teal-900">→ {w.advice}</p>
                    </div>
                    {devId && canEdit && (
                      <button type="button" onClick={() => dismiss(w.key)} title="この案件では表示しない"
                        className="shrink-0 opacity-60 hover:opacity-100"
                        data-testid={`${testid}-dismiss-${w.key}`}>
                        <EyeSlash size={14} />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-center gap-2 text-xs">
            {d.warnings.length > 3 && (
              <button type="button" onClick={() => setShowAll(!showAll)}
                className="font-semibold text-teal-800 underline" data-testid={`${testid}-showall`}>
                {showAll ? '重要な3件だけ表示' : `すべて表示（${d.warnings.length}件）`}
              </button>
            )}
            {d.dismissed_count > 0 && devId && canEdit && (
              <button type="button" onClick={restore} className="text-zinc-600 underline"
                data-testid={`${testid}-restore`}>
                非表示 {d.dismissed_count}件を戻す
              </button>
            )}
            {devId && canEdit && (
              <Button variant="outline" size="sm" className="h-7 text-xs"
                onClick={() => setDraft({
                  title: d.warnings[0]?.title?.slice(0, 60) || '開発の学び',
                  problem: d.warnings.map((w) => w.title).join('\n'),
                  countermeasure: d.warnings.map((w) => w.advice).join('\n'),
                })}
                data-testid={`${testid}-to-knowledge`}>
                <BookOpen size={13} className="mr-1" /> この学びをナレッジ記事にする
              </Button>
            )}
          </div>

          {d.similar_projects.length > 0 && (
            <div data-testid={`${testid}-similar`}>
              <p className="text-xs font-semibold text-zinc-700">近い過去案件</p>
              <ul className="mt-1 space-y-0.5">
                {d.similar_projects.map((s) => (
                  <li key={s.development_id} className="text-sm">
                    <Link to={`/engineering/developments/${s.development_id}`}
                      className="font-semibold text-zinc-900 underline"
                      data-testid={`${testid}-similar-${s.development_id}`}>{s.label}</Link>
                    <span className="ml-2 text-xs text-zinc-600">
                      {s.same_type ? '同じ種類' : ''}
                      {s.shared_parts.length ? ` · 共通部品 ${s.shared_parts.join('、')}` : ''}
                      {` · ${s.completed ? '完了' : s.stage}`}
                      {` · 不具合 ${s.failures}件 / 改訂 ${s.revisions}回`}
                      {s.final_cost ? ` · ${yen(s.final_cost)}` : ''}
                      {s.lead_time_days !== null && s.lead_time_days !== undefined
                        ? ` · ${s.lead_time_days}日` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {d.cost && !compact && (
            <p className="text-sm text-zinc-900" data-testid={`${testid}-cost`}>
              <CurrencyJpy size={13} className="mr-1 inline text-teal-700" />
              同じ種類（{d.cost.type_label}）の過去 {d.cost.projects}件の平均原価は
              <span className="font-bold"> {yen(d.cost.avg_cost)}</span>
              <span className="text-xs text-zinc-600">（最小 {yen(d.cost.min_cost)} / 最大 {yen(d.cost.max_cost)}）</span>
            </p>
          )}

          {!compact && k?.hits?.length > 0 && (
            <div data-testid={`${testid}-knowledge`}>
              <p className="text-xs font-semibold text-zinc-700">
                <Files size={12} className="mr-1 inline text-teal-700" />
                関連しそうな社内資料（ナレッジ・書類・日報）
              </p>
              <ul className="mt-1 space-y-0.5">
                {k.hits.map((h) => (
                  <li key={h.source_key} className="text-sm">
                    <a href={h.url || '#'} target="_blank" rel="noreferrer"
                      className="text-teal-900 underline"
                      data-testid={`${testid}-doc-${h.source_key}`}>
                      {h.title || '無題'} <ArrowSquareOut size={10} className="inline" />
                    </a>
                    <span className="ml-1 text-xs text-zinc-600">
                      {h.module_label || h.module} · 一致「{h.matched_term}」
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {d.ai_commentary ? (
            <p className="rounded-md border border-teal-300 bg-white px-3 py-2 text-sm text-zinc-800"
              data-testid={`${testid}-ai`}>
              <Sparkle size={13} className="mr-1 inline text-teal-700" />
              <span className="font-semibold">AIコメント：</span>{d.ai_commentary}
            </p>
          ) : (
            <p className="text-xs text-zinc-500" data-testid={`${testid}-ai-unavailable`}>
              AIコメントは利用できません（上の事実はデータから直接集計しています）。
            </p>
          )}

          <Link to="/engineering/developments/learning"
            className="inline-block text-xs font-semibold text-teal-800 underline"
            data-testid={`${testid}-digest-link`}>学びのダイジェストを開く</Link>
        </div>
      )}

      <KnowledgeDraftDialog devId={devId} initial={draft} onClose={() => setDraft(null)} />
    </section>
  );
};
